import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '@/services/api';
import { QueryFilter } from '@/types';

export default function QueryFilters() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<QueryFilter | null>(null);
  const [filterToDelete, setFilterToDelete] = useState<QueryFilter | null>(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectKeys, setProjectKeys] = useState('');
  const [issueTypes, setIssueTypes] = useState('');
  const [triggerStatus, setTriggerStatus] = useState('Ready for UAT');
  const [additionalJql, setAdditionalJql] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const { data: filters, isLoading, error } = useQuery({
    queryKey: ['queryFilters'],
    queryFn: () => api.getQueryFilters(),
  });

  const createMutation = useMutation({
    mutationFn: (filter: Partial<QueryFilter>) => api.createQueryFilter(filter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryFilters'] });
      handleCloseDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (filter: QueryFilter) => api.updateQueryFilter(filter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryFilters'] });
      handleCloseDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteQueryFilter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queryFilters'] });
      setDeleteDialogOpen(false);
      setFilterToDelete(null);
    },
  });

  const handleOpenDialog = (filter?: QueryFilter) => {
    if (filter) {
      setEditingFilter(filter);
      setName(filter.name);
      setDescription(filter.description || '');
      setProjectKeys(filter.projectKeys?.join(', ') || '');
      setIssueTypes(filter.issueTypes?.join(', ') || '');
      setTriggerStatus(filter.triggerStatus || 'Ready for UAT');
      setAdditionalJql(filter.additionalJql || '');
      setIsDefault(filter.isDefault);
    } else {
      setEditingFilter(null);
      setName('');
      setDescription('');
      setProjectKeys('');
      setIssueTypes('');
      setTriggerStatus('Ready for UAT');
      setAdditionalJql('');
      setIsDefault(false);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFilter(null);
    setName('');
    setDescription('');
    setProjectKeys('');
    setIssueTypes('');
    setTriggerStatus('Ready for UAT');
    setAdditionalJql('');
    setIsDefault(false);
  };

  const handleSave = () => {
    const filterData: Partial<QueryFilter> = { 
      name, 
      description: description || undefined,
      projectKeys: projectKeys.split(',').map(k => k.trim()).filter(k => k),
      issueTypes: issueTypes.split(',').map(t => t.trim()).filter(t => t),
      triggerStatus,
      additionalJql: additionalJql || undefined,
      isDefault,
      isActive: true,
    };
    if (editingFilter) {
      updateMutation.mutate({ ...editingFilter, ...filterData } as QueryFilter);
    } else {
      createMutation.mutate(filterData);
    }
  };

  const handleDeleteClick = (filter: QueryFilter) => {
    setFilterToDelete(filter);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading query filters: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Query Filters
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Filter
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Query filters define the criteria for fetching issues from Jira.
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Trigger Status</TableCell>
              <TableCell>Projects</TableCell>
              <TableCell>Default</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(filters) && filters.length > 0 ? (
              filters.map((filter) => (
                <TableRow key={filter.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{filter.name}</Typography>
                      {filter.description && (
                        <Typography variant="caption" color="text.secondary">
                          {filter.description}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={filter.triggerStatus} 
                      size="small" 
                      sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
                    />
                  </TableCell>
                  <TableCell>
                    {filter.projectKeys?.join(', ') || '-'}
                  </TableCell>
                  <TableCell>{filter.isDefault ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog(filter)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(filter)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No filters configured</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFilter ? 'Edit Filter' : 'Add Filter'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              helperText="Optional description of this filter"
            />
            <TextField
              label="Project Keys"
              value={projectKeys}
              onChange={(e) => setProjectKeys(e.target.value)}
              fullWidth
              helperText="Comma-separated project keys (e.g., UP, PROJ)"
            />
            <TextField
              label="Issue Types"
              value={issueTypes}
              onChange={(e) => setIssueTypes(e.target.value)}
              fullWidth
              helperText="Comma-separated issue types (e.g., Story, Bug, Task)"
            />
            <TextField
              label="Trigger Status"
              value={triggerStatus}
              onChange={(e) => setTriggerStatus(e.target.value)}
              fullWidth
              helperText="Status that triggers release note generation"
            />
            <TextField
              label="Additional JQL"
              value={additionalJql}
              onChange={(e) => setAdditionalJql(e.target.value)}
              multiline
              rows={2}
              fullWidth
              helperText="Additional JQL conditions to append"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              }
              label="Set as default filter"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete filter <strong>{filterToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => filterToDelete && deleteMutation.mutate(filterToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
